/**
 * BackgroundGenerationIndicator Component
 *
 * Compact header indicator showing background course generation progress.
 * Displays in the app header when courses are being generated.
 *
 * @author Tutara Team
 * @since January 2025
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, X, CheckCircle, AlertCircle, ChevronDown, Sparkles, RotateCcw } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { TutaraProgress } from '@/components/tutara-ui/core/TutaraProgress';
import { TutaraButton } from '@/components/tutara-ui/core/TutaraButton';
import { useCourseGeneration } from '@/hooks/useCourseGeneration';
import {
  useCourseGenerationStore,
  type CourseGenerationJob,
} from '@/stores/course-generation-store';
import { cn } from '@/utils/cn';

/** Mirrors MAX_RETRY_ATTEMPTS in useCourseGeneration — kept local to drive the
 * conditional Retry button without exporting an extra constant. */
const MAX_RETRY_ATTEMPTS = 3;

// =============================================================================
// Sub-components
// =============================================================================

interface JobItemProps {
  job: CourseGenerationJob;
  onCancel: (jobId: string) => void;
  onDismiss: (jobId: string) => void;
  onRetry: (jobId: string) => void;
  onSignIn: () => void;
}

function JobItem({ job, onCancel, onDismiss, onRetry, onSignIn }: JobItemProps) {
  const isActive = job.status === 'queued' || job.status === 'connecting' || job.status === 'generating';
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed' || job.status === 'cancelled';

  return (
    <div
      className={cn(
        'p-3 border-b border-gray-100 dark:border-tutara-dark-mode-border last:border-0',
        isCompleted && 'bg-green-50/50 dark:bg-green-900/10',
        isFailed && 'bg-red-50/50 dark:bg-red-900/10'
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isActive && <Loader2 className="w-4 h-4 text-tutara-primary animate-spin flex-shrink-0" />}
          {isCompleted && <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />}
          {isFailed && <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
          <span className="text-sm font-medium text-tutara-charcoal dark:text-white truncate">
            {job.courseTopic}
          </span>
        </div>

        {/* Actions */}
        {isActive && (
          <button
            onClick={() => onCancel(job.id)}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Cancel generation"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {(isCompleted || isFailed) && (
          <button
            onClick={() => onDismiss(job.id)}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Status / Progress */}
      {isActive && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
              {job.currentStep}
            </span>
            <span className="font-medium text-tutara-primary ml-2">{job.progress}%</span>
          </div>
          <TutaraProgress value={job.progress} size="sm" />
        </div>
      )}

      {isCompleted && (
        <p className="text-xs text-green-600 dark:text-green-400">Course generated successfully!</p>
      )}

      {isFailed && (
        <div className="space-y-2">
          <p className="text-xs text-red-500 dark:text-red-400">
            {job.errorMessage || 'Generation failed'}
          </p>
          {/* Action: Retry for transient/retryable errors, Sign in for UNAUTHORIZED.
              For 'cancelled' status (no errorCode), nothing is offered. */}
          {job.status === 'failed' && job.errorCode === 'UNAUTHORIZED' && (
            <TutaraButton
              variant="secondary"
              size="sm"
              onClick={onSignIn}
              className="text-xs"
            >
              Sign in
            </TutaraButton>
          )}
          {job.status === 'failed'
            && job.errorCode !== 'UNAUTHORIZED'
            && (job.retryCount ?? 0) < MAX_RETRY_ATTEMPTS && (
              <TutaraButton
                variant="secondary"
                size="sm"
                onClick={() => onRetry(job.id)}
                className="text-xs gap-1.5"
              >
                <RotateCcw className="w-3 h-3" />
                {(job.retryCount ?? 0) > 0
                  ? `Retry · attempt ${(job.retryCount ?? 0) + 1} of ${MAX_RETRY_ATTEMPTS}`
                  : 'Retry'}
              </TutaraButton>
            )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function BackgroundGenerationIndicator() {
  const [isExpanded, setIsExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { activeJobsCount, hasActiveJobs, cancelGenerationJob, dismissJob, retryJob } = useCourseGeneration();
  const navigate = useNavigate();
  const handleSignIn = () => {
    setIsExpanded(false);
    navigate('/login');
  };

  // Use useShallow to prevent infinite loops from array selector
  const jobs = useCourseGenerationStore(useShallow((state) => state.jobs));
  const recentJobs = useMemo(() => jobs.slice(-5).reverse(), [jobs]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    }

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  // Don't render if no jobs at all
  if (recentJobs.length === 0) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Compact indicator button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors',
          hasActiveJobs
            ? 'bg-tutara-primary/10 hover:bg-tutara-primary/20'
            : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
        )}
        aria-label={hasActiveJobs ? `${activeJobsCount} courses generating` : 'Recent course generations'}
      >
        {hasActiveJobs ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin text-tutara-primary" />
            <span className="text-sm font-medium text-tutara-primary">
              {activeJobsCount} generating
            </span>
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm text-gray-600 dark:text-gray-400">Recent</span>
          </>
        )}
        <ChevronDown
          className={cn(
            'w-4 h-4 transition-transform text-gray-500 dark:text-gray-400',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown */}
      {isExpanded && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-tutara-dark-mode-surface rounded-lg shadow-xl border border-gray-200 dark:border-tutara-dark-mode-border z-50">
          {/* Header */}
          <div className="p-3 border-b border-gray-200 dark:border-tutara-dark-mode-border">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-tutara-charcoal dark:text-white text-sm">
                Course Generation
              </h4>
              {hasActiveJobs && (
                <span className="text-xs bg-tutara-primary/10 text-tutara-primary px-2 py-0.5 rounded-full">
                  {activeJobsCount} active
                </span>
              )}
            </div>
          </div>

          {/* Job list */}
          <div className="max-h-80 overflow-y-auto">
            {recentJobs.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No recent course generations
              </div>
            ) : (
              recentJobs.map((job) => (
                <JobItem
                  key={job.id}
                  job={job}
                  onCancel={cancelGenerationJob}
                  onDismiss={dismissJob}
                  onRetry={(id) => { void retryJob(id); }}
                  onSignIn={handleSignIn}
                />
              ))
            )}
          </div>

          {/* Footer */}
          {recentJobs.length > 0 && !hasActiveJobs && (
            <div className="p-2 border-t border-gray-200 dark:border-tutara-dark-mode-border">
              <TutaraButton
                variant="ghost"
                size="sm"
                onClick={() => {
                  useCourseGenerationStore.getState().clearCompletedJobs();
                  setIsExpanded(false);
                }}
                className="w-full text-xs"
              >
                Clear history
              </TutaraButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BackgroundGenerationIndicator;
